import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getInvoice,
  getCategories,
  getAudit,
  updateLineItem,
  approveInvoice,
  rejectInvoice,
  money, baseMoney,
  STATUS_META,
  correctInvoiceFields,
  editLineItem,
  formatApiError,
} from "@/lib/apiClient";
import { useApp } from "@/context/AppContext";
import { ConfidenceBadge, ConfidenceBar, confidenceTier } from "@/components/ConfidenceBadge";
import { CommentsThread } from "@/components/CommentsThread";
import { AssignCard } from "@/components/AssignCard";
import { ExportPreviewDialog } from "@/components/ExportPreviewDialog";
import { XeroSyncButton } from "@/components/XeroSyncButton";
import { XeroDriftCard } from "@/components/XeroDriftCard";
import { AccountingSyncButton } from "@/components/AccountingSyncButton";
import { LedgerChip } from "@/components/LedgerRouting";
import { LedgerLiveCheck } from "@/components/LedgerLiveCheck";
import { ProviderLogo } from "@/components/ProviderLogo";
import { useXeroTracking, TrackingPicker, LineTrackingSelect } from "@/components/XeroTracking";
import { SplitLineDialog } from "@/components/SplitLineDialog";
import { PaymentCard } from "@/components/PaymentCard";
import { FxRateCard } from "@/components/FxRateCard";
import { DuplicateDecision } from "@/components/DuplicateDecision";
import { SupplierNotifications } from "@/components/SupplierNotifications";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  ArrowLeft,
  Check,
  X,
  FileText,
  Mail,
  Loader2,
  History,
  Pencil,
  Sparkles,
  Building2,
  Download,
  Zap,
  CheckCircle2,
  Scissors,
  AlertTriangle,
} from "lucide-react";
import { DocumentViewer } from "@/components/DocumentViewer";
import { EditableField } from "@/components/EditableField";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const ACTION_META = {
  extracted: { color: "text-indigo-400", label: "Extracted" },
  auto_coded: { color: "text-sky-400", label: "Auto-Coded" },
  flagged: { color: "text-amber-500", label: "Flagged" },
  recoded: { color: "text-purple-400", label: "Recoded" },
  approved: { color: "text-emerald-500", label: "Approved" },
  rejected: { color: "text-red-500", label: "Rejected" },
  exported: { color: "text-cyan-400", label: "Exported" },
  commented: { color: "text-pink-400", label: "Comment" },
  assigned: { color: "text-violet-400", label: "Assigned" },
  paid: { color: "text-emerald-400", label: "Paid" },
  notified: { color: "text-rose-400", label: "Emailed" },
  xero_drift: { color: "text-amber-500", label: "Changed in Xero" },
  export_failed: { color: "text-red-500", label: "Sync Failed" },
  tracking: { color: "text-sky-400", label: "Tracking" },
};

const ERP_OPTIONS = [
  { key: "xero", label: "Xero CSV" },
  { key: "freshbooks", label: "FreshBooks CSV" },
  { key: "zoho", label: "Zoho Books CSV" },
  { key: "sage", label: "Sage CSV" },
];

const Field = ({ label, value, mono = true }) => (
  <div>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={cn("text-sm font-medium mt-0.5 break-words", mono && "font-mono")}>{value || "—"}</div>
  </div>
);

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useApp();
  const [inv, setInv] = useState(null);
  const [cats, setCats] = useState([]);
  const [audit, setAudit] = useState([]);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [exporting] = useState(null);
  const [previewSystem, setPreviewSystem] = useState(null);
  const [splitting, setSplitting] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const xeroTracking = useXeroTracking();

  const correctedBy = (field) => (inv?.corrections || []).filter((c) => c.field === field).slice(-1)[0];
  const saveField = async (field, value) => {
    try {
      const updated = await correctInvoiceFields(id, { [field]: value, user: user?.name || user?.email || "Finance User" });
      setInv(updated);
      setAudit(await getAudit(id));
      toast.success(`${field.replace("_", " ")} corrected — D20 will remember this for ${updated.supplier}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, "Could not save correction"));
      throw e;
    }
  };

  const lineCorrected = (lineId, field) => (inv?.corrections || []).filter((c) => c.line_id === lineId && c.field === `line.${field}`).slice(-1)[0];
  const saveLine = async (lineId, body) => {
    try {
      const updated = await editLineItem(id, lineId, { ...body, user: user?.name || user?.email || "Finance User" });
      setInv(updated);
      setAudit(await getAudit(id));
      toast.success("Line corrected");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, "Could not save"));
      throw e;
    }
  };

  const refresh = useCallback(async () => {
    try {
      const [i, a] = await Promise.all([getInvoice(id), getAudit(id)]);
      setInv(i);
      setAudit(a);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error("Could not load invoice");
    }
  }, [id]);

  useEffect(() => {
    getCategories().then(setCats);
    refresh();
  }, [refresh]);

  const changeCode = async (itemId, gl_code) => {
    try {
      const updated = await updateLineItem(id, itemId, gl_code, user);
      setInv(updated);
      const a = await getAudit(id);
      setAudit(a);
      toast.success("GL code updated & logged");
    } catch (e) {
      toast.error("Could not update code");
    }
  };

  const decide = async (approve) => {
    setBusy(true);
    try {
      const fn = approve ? approveInvoice : rejectInvoice;
      const updated = await fn(id, user, comment || null);
      setInv(updated);
      const a = await getAudit(id);
      setAudit(a);
      setComment("");
      approve
        ? toast.success(`Invoice approved by ${user}`)
        : toast.error(`Invoice rejected by ${user}`);
    } finally {
      setBusy(false);
    }
  };

  const handleExport = (system) => setPreviewSystem(system);

  if (notFound) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-center" data-testid="invoice-not-found">
        <FileText className="h-10 w-10 text-muted-foreground mb-4" />
        <h2 className="text-lg font-semibold">Invoice not found</h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-sm">This invoice may have been deleted, or the link points to a record that isn't an invoice.</p>
        <Button data-testid="invoice-not-found-back" variant="outline" className="mt-6" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Go back
        </Button>
      </div>
    );
  }

  if (!inv) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const meta = STATUS_META[inv.status];
  const decided = inv.status === "approved" || inv.status === "rejected";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          data-testid="back-btn"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="flex items-center gap-3">
          {inv.escalated && inv.status === "pending_review" && (
            <span data-testid="detail-escalated" title={`Escalated from ${inv.escalated.from || "unassigned"} on ${new Date(inv.escalated.at).toLocaleString()}`}
                  className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-sm font-medium text-red-500">Escalated → {inv.escalated.to}</span>
          )}
          <ConfidenceBadge value={inv.min_confidence} testId="detail-confidence-badge" />
          <span className={cn("rounded-lg border px-3 py-1.5 text-sm font-medium", meta.cls)} data-testid="detail-status">
            {meta.label}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Building2 className="h-6 w-6" />
        </span>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight" data-testid="detail-supplier">
            {inv.supplier || "Unknown supplier"}
          </h1>
          <p className="text-sm text-muted-foreground font-mono">
            {inv.invoice_number} · {money(inv.total, inv.currency)}
            {baseMoney(inv) && <span data-testid="detail-base-total"> · ≈ {baseMoney(inv)}</span>}
          </p>
        </div>
      </div>

      <DuplicateDecision inv={inv} user={user} onResolved={(u) => { setInv(u); getAudit(id).then(setAudit).catch(() => {}); }} />

      {inv.split_group && (
        <div data-testid="split-badge" className="flex items-center gap-2 rounded-xl border border-sky-500/40 bg-sky-500/10 px-4 py-2.5 text-sm">
          <Scissors className="h-4 w-4 text-sky-400" />
          <span>Part <b>{inv.split_group.part}</b> of <b>{inv.split_group.of}</b> — split from <span className="font-mono">{inv.split_group.filename}</span> (pages {inv.split_group.pages?.join(", ")}).</span>
        </div>
      )}
      {inv.learned?.length > 0 && (
        <div data-testid="learned-notes" className="rounded-xl border border-sky-500/40 bg-sky-500/10 px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-medium text-sky-400"><Sparkles className="h-4 w-4" /> Applied from past corrections</div>
          <ul className="mt-1.5 space-y-0.5 text-sm">{inv.learned.map((r) => <li key={r} className="flex gap-2"><span className="text-sky-400">•</span><span>{r}</span></li>)}</ul>
        </div>
      )}
      {inv.review_reasons?.length > 0 && inv.status === "pending_review" && (
        <div data-testid="review-reasons" className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-medium text-amber-500"><AlertTriangle className="h-4 w-4" /> Why this needs a human</div>
          <ul className="mt-1.5 space-y-0.5 text-sm">
            {inv.review_reasons.map((r) => <li key={r} className="flex gap-2" data-testid="review-reason"><span className="text-amber-500">•</span><span>{r}</span></li>)}
          </ul>
        </div>
      )}

      {/* Split screen */}
      <div className="grid gap-5 lg:grid-cols-2 min-w-0 [&>*]:min-w-0">
        {/* Left: document */}
        <DocumentViewer inv={inv} user={user?.name || user?.email || "Finance User"} />

        {/* Right: extracted */}
        <div className="space-y-5">
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 text-sm font-medium mb-4">
              <Sparkles className="h-4 w-4 text-primary" /> Extracted Fields
            </div>
            <div className="grid grid-cols-2 gap-4">
              <EditableField label="Invoice #" field="invoice_number" value={inv.invoice_number} corrected={correctedBy("invoice_number")} onSave={saveField} />
              <EditableField label="Supplier" field="supplier" value={inv.supplier} mono={false} corrected={correctedBy("supplier")} onSave={saveField} />
              <EditableField label="Invoice Date" field="invoice_date" type="date" value={inv.invoice_date} corrected={correctedBy("invoice_date")} onSave={saveField} />
              <EditableField label="Due Date" field="due_date" type="date" value={inv.due_date} corrected={correctedBy("due_date")} onSave={saveField} />
              <EditableField label="Subtotal" field="subtotal" type="number" value={inv.subtotal} display={money(inv.subtotal, inv.currency)} corrected={correctedBy("subtotal")} onSave={saveField} />
              <EditableField label="Tax" field="tax" type="number" value={inv.tax} display={money(inv.tax, inv.currency)} corrected={correctedBy("tax")} onSave={saveField} />
              <EditableField label="Currency" field="currency" value={inv.currency} corrected={correctedBy("currency")} onSave={saveField} />
              <EditableField label="Total" field="total" type="number" value={inv.total} display={money(inv.total, inv.currency)} corrected={correctedBy("total")} onSave={saveField} />
              {inv.base_currency && inv.currency !== inv.base_currency && (
                <>
                  <Field label={`Total in ${inv.base_currency}`} value={inv.base_total != null ? money(inv.base_total, inv.base_currency) : "rate unavailable"} />
                  <Field label="FX rate" value={inv.fx_rate ? `${inv.fx_rate} · ${inv.fx_locked ? `bank rate (locked by ${inv.fx_locked.by})` : `ECB ${inv.fx_date}`}` : "—"} />
                </>
              )}
            </div>
          </div>

          <TrackingPicker inv={inv} user={user} tracking={xeroTracking} onChanged={(u) => { setInv(u); getAudit(id).then(setAudit); }} />

          {/* Decision */}
          <div className="rounded-xl border border-border bg-card p-5">
            {decided ? (
              <div className="text-sm">
                <div className="flex items-center gap-2 font-medium">
                  {inv.status === "approved" ? (
                    <Check className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <X className="h-4 w-4 text-red-500" />
                  )}
                  {meta.label} by {inv.reviewed_by}
                  {inv.auto_approved && (
                    <span className="ml-1 inline-flex items-center gap-1 rounded-md bg-primary/15 text-primary px-2 py-0.5 text-xs">
                      <Zap className="h-3 w-3" /> Auto
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1 font-mono">
                  {inv.reviewed_at ? new Date(inv.reviewed_at).toLocaleString() : ""}
                </p>
              </div>
            ) : (
              <>
                <Textarea
                  data-testid="decision-comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Optional reviewer note…"
                  className="mb-3 min-h-[70px] resize-none text-sm"
                />
                <div className="flex gap-3">
                  <Button
                    data-testid="approve-btn"
                    disabled={busy}
                    onClick={() => decide(true)}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                    Approve
                  </Button>
                  <Button
                    data-testid="reject-btn"
                    disabled={busy}
                    variant="outline"
                    onClick={() => decide(false)}
                    className="flex-1 border-red-500/40 text-red-500 hover:bg-red-500/10"
                  >
                    <X className="h-4 w-4 mr-2" /> Reject
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Hand-off */}
          <AssignCard inv={inv} onChanged={(u) => { setInv(u); getAudit(id).then(setAudit); }} />

          {/* Payment */}
          <PaymentCard inv={inv} onChanged={(u) => { setInv(u); getAudit(id).then(setAudit); }} />

          {/* FX rate lock */}
          <FxRateCard inv={inv} onChanged={(u) => { setInv(u); getAudit(id).then(setAudit); }} />

          {/* Supplier emails */}
          <SupplierNotifications inv={inv} onChanged={() => getAudit(id).then(setAudit)} />

          {/* ERP Export */}
          <div className="rounded-xl border border-border bg-card p-5" data-testid="erp-export-card">
            <div className="flex items-center gap-2 text-sm font-medium mb-1">
              <Zap className="h-4 w-4 text-cyan-400" /> ERP Export
            </div>
            <XeroDriftCard inv={inv} user={user} onChanged={(u) => { setInv(u); getAudit(id).then(setAudit); }} />
            <LedgerChip inv={inv} />
            <LedgerLiveCheck inv={inv} onRefreshed={refresh} />
            <XeroSyncButton inv={inv} user={user} onSynced={refresh} />
            <XeroDriftCard inv={inv} user={user} provider="freshbooks" label="FreshBooks" onChanged={(u) => { setInv(u); getAudit(id).then(setAudit); }} />
            <AccountingSyncButton provider="freshbooks" inv={inv} user={user} onSynced={refresh} />
            <XeroDriftCard inv={inv} user={user} provider="zoho" label="Zoho Books" onChanged={(u) => { setInv(u); getAudit(id).then(setAudit); }} />
            <AccountingSyncButton provider="zoho" inv={inv} user={user} onSynced={refresh} />
            <XeroDriftCard inv={inv} user={user} provider="sage" label="Sage Accounting" onChanged={(u) => { setInv(u); getAudit(id).then(setAudit); }} />
            <AccountingSyncButton provider="sage" inv={inv} user={user} onSynced={refresh} />
            {inv.export_status ? (
              <div className="mb-3 flex items-center gap-2 text-sm text-emerald-500" data-testid="export-status">
                <CheckCircle2 className="h-4 w-4" />
                Synced to {inv.export_status.system_label} ·{" "}
                <span className="font-mono text-xs text-muted-foreground">
                  {new Date(inv.export_status.exported_at).toLocaleString()}
                </span>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground mb-3">
                {decided
                  ? "Fallback: download an import file (CSV) for Xero or FreshBooks — it opens in Excel. If your accounting system is connected, the direct sync above is all you need. Every export is kept in the history below."
                  : "Approve the invoice first to enable ERP export."}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {ERP_OPTIONS.map((o) => (
                <Button
                  key={o.key}
                  data-testid={`export-${o.key}-btn`}
                  variant="outline"
                  size="sm"
                  disabled={inv.status !== "approved" || exporting}
                  onClick={() => handleExport(o.key)}
                >
                  {exporting === o.key ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <ProviderLogo provider={o.key} className="mr-1.5" />
                  )}
                  {o.label}
                  <Download className="h-3 w-3 ml-1.5 text-muted-foreground" />
                </Button>
              ))}
            </div>
            {inv.export_history?.length > 0 && (
              <div className="mt-4 border-t border-border pt-3" data-testid="export-history">
                <div className="text-xs font-medium text-muted-foreground mb-2">
                  Export history · {inv.export_history.length}
                </div>
                <ul className="space-y-1.5">
                  {[...inv.export_history].reverse().map((h, idx) => (
                    <li
                      key={h.id || idx}
                      data-testid="export-history-item"
                      className="flex items-center justify-between gap-3 text-xs"
                    >
                      <span className="flex items-center gap-1.5 min-w-0">
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                        <span className="font-medium">{h.system_label}</span>
                        <span className="text-muted-foreground truncate">by {h.exported_by}</span>
                        {h.exported_total != null && (
                          <span className="font-mono text-muted-foreground">· {h.rows} rows · {money(h.exported_total, h.currency || inv.currency)}</span>
                        )}
                      </span>
                      <span className="font-mono text-muted-foreground whitespace-nowrap">
                        {new Date(h.exported_at).toLocaleString()}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Line items */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="border-b border-border px-5 py-3 text-sm font-medium flex items-center justify-between">
          <span>Line Items & GL Coding</span>
          <span className="text-xs text-muted-foreground">Click a code to override — every change is logged</span>
        </div>
        <div className="overflow-x-auto d20-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="px-5 py-3 font-medium">Description</th>
                <th className="px-3 py-3 font-medium text-right">Amount</th>
                <th className="px-3 py-3 font-medium">Suggested GL Code</th>
                <th className="px-5 py-3 font-medium">Confidence</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {inv.line_items.map((li) => {
                const tier = confidenceTier(li.confidence);
                return (
                  <tr key={li.id} data-testid={`lineitem-row-${li.id}`} className="align-top">
                    <td className="px-5 py-4">
                      {decided ? <div className="font-medium">{li.description}</div> : (
                        <EditableField field={`line-${li.id}-description`} value={li.description} mono={false} compact onSave={(_, v) => saveLine(li.id, { description: v })} corrected={lineCorrected(li.id, "description")} />
                      )}
                      <div className="text-xs text-muted-foreground font-mono mt-0.5 flex items-center gap-1">
                        {decided ? <>{li.quantity} × {money(li.unit_price, inv.currency)}</> : (
                          <>
                            <EditableField field={`line-${li.id}-quantity`} type="number" value={li.quantity} compact onSave={(_, v) => saveLine(li.id, { quantity: v })} corrected={lineCorrected(li.id, "quantity")} />
                            <span>×</span>
                            <EditableField field={`line-${li.id}-unit_price`} type="number" value={li.unit_price} display={money(li.unit_price, inv.currency)} compact onSave={(_, v) => saveLine(li.id, { unit_price: v })} corrected={lineCorrected(li.id, "unit_price")} />
                          </>
                        )}
                      </div>
                      {li.reasoning && (
                        <div className="text-xs text-muted-foreground/80 mt-1 italic">{li.reasoning}</div>
                      )}
                      {!decided && (
                        <button
                          data-testid={`split-line-btn-${li.id}`}
                          onClick={() => setSplitting(li)}
                          className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                        >
                          <Scissors className="h-3 w-3" /> Split across GL codes
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-4 text-right font-mono font-semibold whitespace-nowrap">
                      {decided ? money(li.amount, inv.currency) : (
                        <EditableField field={`line-${li.id}-amount`} type="number" value={li.amount} display={money(li.amount, inv.currency)} compact align="right" onSave={(_, v) => saveLine(li.id, { amount: v })} corrected={lineCorrected(li.id, "amount")} />
                      )}
                    </td>
                    <td className="px-3 py-4">
                      <Select value={li.gl_code} onValueChange={(v) => changeCode(li.id, v)}>
                        <SelectTrigger
                          data-testid={`gl-select-${li.id}`}
                          className={cn(
                            "w-[230px] font-mono text-xs",
                            tier !== "high" && "border-amber-500/50"
                          )}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {cats.map((c) => (
                            <SelectItem key={c.code} value={c.code} className="font-mono text-xs">
                              {c.code} — {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <LineTrackingSelect inv={inv} li={li} user={user} tracking={xeroTracking} onChanged={(u) => { setInv(u); getAudit(id).then(setAudit); }} />
                    </td>
                    <td className="px-5 py-4">
                      <div className="w-28 space-y-1.5">
                        <ConfidenceBadge value={li.confidence} showLabel={false} testId={`lineitem-confidence-${li.id}`} />
                        <ConfidenceBar value={li.confidence} />
                        {tier !== "high" && (
                          <span className="text-[11px] text-amber-500 flex items-center gap-1">
                            <Pencil className="h-3 w-3" /> verify code
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reviewer notes */}
      <CommentsThread invoiceId={id} onChange={() => getAudit(id).then(setAudit)} />

      <ExportPreviewDialog
        invoiceId={id}
        system={previewSystem}
        currency={inv.currency}
        onClose={() => setPreviewSystem(null)}
        onExported={{ user, done: refresh }}
      />
      {splitting && (
        <SplitLineDialog
          invoiceId={id}
          line={splitting}
          currency={inv.currency}
          categories={cats}
          user={user}
          onClose={() => setSplitting(null)}
          onDone={(updated) => { setInv(updated); getAudit(id).then(setAudit); }}
        />
      )}

      {/* Audit trail */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="border-b border-border px-5 py-3 text-sm font-medium flex items-center gap-2">
          <History className="h-4 w-4" /> Audit Trail
        </div>
        <div className="divide-y divide-border" data-testid="detail-audit-trail">
          {audit.map((e) => {
            const am = ACTION_META[e.action] || { color: "text-muted-foreground", label: e.action };
            return (
              <div key={e.id} className="flex items-start gap-3 px-5 py-3">
                <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", am.color.replace("text-", "bg-"))} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm">{e.detail}</div>
                  <div className="text-xs text-muted-foreground font-mono mt-0.5">
                    {e.user} · {new Date(e.timestamp).toLocaleString()}
                  </div>
                </div>
                <span className={cn("text-xs font-medium whitespace-nowrap", am.color)}>{am.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
